import { type ReactNode } from "react";
import { type PdpPageProductData } from "~/features/productDetails/models/PdpPageProductData";

import { CompanyLogo } from "~/domains/company/components/CompanyLogo";
import { ExtendedPdpSummary } from "~/features/productDetails/components/ProductSummary/ExtendedPdpSummary";
import { DefaultPdpSummary } from "~/features/productDetails/components/ProductSummary/DefaultPdpSummary";
import { userIsCustomer, userIsSalesman } from "~/domains/user/utils/roles";
import { SalesmanPdpSummary } from "~/features/productDetails/components/ProductSummary/SalesmanPdpSummary";
import { IncludedPartsSection } from "~/features/productDetails/components/Housing/IncludedPartsSection";
import { OptionalPartsSection } from "~/features/productDetails/components/Housing/OptionalPartsSection";
import { isKITProduct } from "~/domains/product/types/ProductData";
import { QuantityChangedSnackbar } from "~/features/productDetails/components/Housing/HousingQtyChangedSnackbar";
import { useTranslation } from "react-i18next";
import { CampaignRecommendations } from "~/features/saleRecommendations/components/CampaignRecommendations/CampaignRecommendations";
import type { CommonProductDetailsPageProps } from "~/pages/ProductDetailsPage/ProductDetailsPage";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { usePdpTabs } from "~/features/productDetails/components/usePDPTabs";
import type { TabId } from "~/features/productDetails/components/usePDPTabs";
import { Stack } from "~/common/mui";
import { Tab } from "~/common/mui/Tabs/Tab";
import { Tabs } from "~/common/mui/Tabs/Tabs";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { ProductDescriptorContext } from "~/domains/product/ProductContext";
import { useScript } from "~/common/hooks/useScript";
import { ProductDetailsRoot } from "~/features/productDetails/components/ProductDetailsPageStyles";
import { isProductPMA } from "~/domains/product/utils/productDescriptorUtils";

export interface ProductDetailsPageProps extends CommonProductDetailsPageProps {
  postSummarySlot?: ReactNode;
  productDescriptor: ProductDescriptor<PdpPageProductData>;
  preferredProductsBlock?: ReactNode;
}

export const DesktopProductDetailsPage = (props: ProductDetailsPageProps) => {
  const productDescriptor = props.productDescriptor;
  const product = productDescriptor.base;

  const { t } = useTranslation();
  const params = new URLSearchParams(window.location.search);
  const activePDPTab = (params.get("activePDPTab") ?? "") as TabId;

  const { tabs, onTabActivationHandler, activeTabIndex } = usePdpTabs(productDescriptor, activePDPTab);

  const { preferredProducts } = props;
  const preferredProductCodes = preferredProducts.data?.preferred.map((product) => product.code) ?? [];
  const hasPreferredProducts = preferredProductCodes.length > 0;

  let summary;
  // Order is important!
  // Special case for Ewellix
  if (product.externalProduct && isProductPMA(productDescriptor)) {
    summary = (
      <DefaultPdpSummary
        productDescriptor={productDescriptor}
        hasPreferredProducts={hasPreferredProducts}
        changeTab={onTabActivationHandler}
      />
    );
  }
  // Salesman is a customer too, so has to be first
  else if (userIsSalesman()) {
    summary = (
      <SalesmanPdpSummary
        productDescriptor={productDescriptor}
        hasPreferredProducts={hasPreferredProducts}
        changeTab={onTabActivationHandler}
      />
    );
  } else {
    const canSeeExtendedPdpSummary = userIsCustomer() && userHasPermission(Permission.AVAILABILITY);

    summary = canSeeExtendedPdpSummary ? (
      <ExtendedPdpSummary
        productDescriptor={productDescriptor}
        hasPreferredProducts={hasPreferredProducts}
        preferredProductCodes={preferredProductCodes}
        changeTab={onTabActivationHandler}
      />
    ) : (
      <DefaultPdpSummary
        productDescriptor={productDescriptor}
        hasPreferredProducts={hasPreferredProducts}
        changeTab={onTabActivationHandler}
      />
    );
  }

  let similarOnSalesBlock = null;
  const products = preferredProducts.data?.sales ?? [];
  if (preferredProducts.data?.showSimilarSalesBlock && products?.length) {
    similarOnSalesBlock = (
      <CampaignRecommendations
        products={preferredProducts.data?.sales ?? undefined}
        variant={"PDP"}
        className={"scha__pdp__similar-sales"}
      />
    );
  }

  useScript("https://orca-cdn.zoovu.com/core-js/zoovu-tag-library/zoovu-tag-library-latest.min.js");

  return (
    <ProductDetailsRoot className="scha__pdp pdp-desktop">
      <div className={"scha__print-only scha__pdp__print-logo"}>
        <CompanyLogo />
      </div>
      <div className={"scha__print-only scha__pdp__timestamp"}>{new Date().toTimeString()}</div>
      <Stack
        sx={{
          gap: { phone: 3, "desktop-s": 4 },
        }}
      >
        {similarOnSalesBlock}
        <Stack gap={4}>
          <ProductDescriptorContext value={productDescriptor}>{summary}</ProductDescriptorContext>
          {isKITProduct(product) && (
            <Stack gap={4}>
              <QuantityChangedSnackbar />
              {(product.includedParts?.length ?? 0) > 0 && (
                <IncludedPartsSection housingProducts={product.includedParts} />
              )}
              {(product.optionalParts?.length ?? 0) > 0 && (
                <OptionalPartsSection housingProducts={product.optionalParts} />
              )}
            </Stack>
          )}
          {props.postSummarySlot}
        </Stack>

        {/* to scroll to tabs from start add to url #tabs at the end */}

        {props.preferredProductsBlock}

        <Tabs
          id="tabs"
          onChange={(e, val) => {
            onTabActivationHandler(val);
          }}
          value={activeTabIndex}
          fullWidthIndicator={false}
          variant={"fullWidth"}
        >
          {tabs.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={t(tab.title)} data-test-id={tab.testId} />
          ))}
        </Tabs>
        {tabs.map((tab) => (
          <div key={tab.value} hidden={activeTabIndex !== tab.value}>
            {tab.component}
          </div>
        ))}
        {/* <ZoovuZoe region="eu" locale="de-DE" sku={product.code} /> */}
      </Stack>
    </ProductDetailsRoot>
  );
};

export const SCROLL_TO_TABS = () => {
  window.setTimeout(() => document.getElementById("tabs")?.scrollIntoView(), 16);
};
